import { inject, Injectable, signal } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { TenantDataMigrationServiceProxy } from "@api/core";
import { PendingMigrationConsent } from "@api/core.models";
import { MigrationConsentDialogComponent, MigrationConsentDialogResult } from "./migration-consent-dialog.component";
import { isMigrationConsentSnoozed } from "./migration-consent-dismissal";

@Injectable({ providedIn: "root" })
export class MigrationConsentService {
    private readonly _api = inject(TenantDataMigrationServiceProxy);
    private readonly _dialog = inject(MatDialog);

    private _info: PendingMigrationConsent | null = null;

    private _dialogRef: MatDialogRef<MigrationConsentDialogComponent, MigrationConsentDialogResult> | null = null;

    readonly pending = signal(false);

    checkAndOpen(): void {
        this._load(() => {
            if (this.pending() && !this._isSnoozed()) {
                this._openDialog();
            }
        });
    }

    open(): void {
        if (this._info) {
            this._openDialog();
            return;
        }
        this._load(() => {
            if (this.pending()) {
                this._openDialog();
            }
        });
    }

    private _isSnoozed(): boolean {
        if (!this._info) {
            return false;
        }

        const lastDismissedAtMs = this._info.lastDismissedAt?.valueOf();
        if (!lastDismissedAtMs) {
            return false;
        }

        return isMigrationConsentSnoozed(this._info.dismissCount ?? 0, lastDismissedAtMs, Date.now());
    }

    private _load(done?: () => void): void {
        this._api.pendingConsent().subscribe({
            next: (info) => {
                this._info = info?.id ? info : null;
                this.pending.set(!!this._info);
                done?.();
            },
            error: () => done?.(),
        });
    }

    private _registerView(): void {
        this._api.noticeViewed().subscribe({ error: () => undefined });
    }

    private _openDialog(): void {
        if (this._dialogRef) {
            return;
        }

        this._dialogRef = this._dialog.open(MigrationConsentDialogComponent, {
            width: "90%",
            maxWidth: "640px",
            panelClass: "migration-consent-dialog",
            closeOnNavigation: false,
            disableClose: true,
        });
        this._registerView();
        this._dialogRef.afterClosed().subscribe((result) => {
            this._dialogRef = null;
            if (result?.decided) {
                this._load();
                return;
            }
            this._api.dismissConsent().subscribe({
                next: (dismissal) => {
                    if (this._info) {
                        this._info.dismissCount = dismissal.count;
                        this._info.lastDismissedAt = dismissal.lastDismissedAt;
                    }
                },
                error: () => this._load(),
            });
        });
    }
}
